import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero, CTABand } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { IMAGES, GARDEN_REAL, YEARS } from "../lib/constants";
import { useTitle } from "../lib/useTitle";

const YEAR_THEMES = [
  { chip: "bg-wing-soft text-wing", bar: "bg-wing", img: IMAGES.sign, alt: "A hand-painted community garden sign welcoming visitors" },
  { chip: "bg-garden-soft text-garden", bar: "bg-garden", img: GARDEN_REAL, alt: "Our real garden space: raised beds blooming with marigolds, pinks, and purple asters" },
  { chip: "bg-pollen-soft text-pollen-dark", bar: "bg-pollen", img: IMAGES.harvest, alt: "An abundant harvest basket of organic vegetables" },
];

export default function Program() {
  useTitle("The Three-Year Program | Bee Courageous @ Garden Grub");
  return (
    <>
      <PageHero
        eyebrow="The Three-Year Program"
        lines={["Three seasons of becoming.", "One courageous woman."]}
        lede="Restoration is not rushed. Our three-year progression gives each woman the time, tools, and community to move from foundation, to growth, to self-sufficiency and leadership."
      />

      <section className="mx-auto max-w-7xl space-y-20 px-4 py-20 sm:px-6 lg:space-y-28 lg:px-12 lg:py-28" data-testid="program-years">
        {YEARS.map((y, i) => {
          const t = YEAR_THEMES[i];
          return (
            <div key={y.num} className={`grid items-center gap-10 lg:grid-cols-12`}>
              <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="arch-frame relative overflow-hidden border-4 border-white shadow-2xl shadow-garden/15">
                  <img src={t.img} alt={t.alt} className="aspect-[4/5] w-full object-cover" loading="lazy" />
                  <span className={`absolute left-4 top-4 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] shadow-lg ${t.chip}`}>
                    {y.num}
                  </span>
                </div>
              </Reveal>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <Reveal delay={0.15}>
                  <div className={`h-1.5 w-16 rounded-full ${t.bar}`} aria-hidden="true" />
                  <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight text-charcoal sm:text-4xl">
                    {y.num}: {y.name}
                  </h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-charcoal/75 sm:text-lg">{y.text}</p>
                  <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {y.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-3 rounded-xl border border-garden/10 bg-white px-4 py-3 text-sm font-semibold text-charcoal/80">
                        <Check className="h-4 w-4 shrink-0 text-garden" aria-hidden="true" /> {pt}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </div>
          );
        })}
      </section>

      <section className="bg-garden-soft/60 py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold text-charcoal sm:text-4xl">Is this journey for you — or someone you love?</h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/70 sm:text-lg">
              Applications are open and judgment-free. Professionals may also refer a woman through our referral pathway.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/get-involved#apply" className="btn-primary" data-testid="program-apply-btn">
                Start an Application <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/referrals" className="btn-outline" data-testid="program-referral-btn">Refer a Participant</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Sponsor a woman's three-year journey."
        text="Your giving covers meals, garden supplies, coaching, and training for women rebuilding their lives."
        primaryLabel="Support the Program"
        primaryTo="/donate"
      />
    </>
  );
}
